import { useRef, useState, useEffect } from "react"
import CryptoJS from "crypto-js";
import { browserName, isMobile } from "react-device-detect";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { Mail, Eye, EyeOff, ArrowRight } from "lucide-react"
import { getDeviceInfo } from "../utils/deviceInfo";
import { useAuth } from "../provider/useAuth";
import { messagingPromise, getFirebaseToken, onMessage } from "../firebase/firebase";
import AuthSlider from "./auth-slider";


function NewLogin() {
  const navigate = useNavigate();
  const location = useLocation();
  const emailRef = useRef();
  const passwordRef = useRef();
  const [showPassword, setShowPassword] = useState(false);
  const [emailError, setEmailError] = useState(null);
  const [passwordError, setPasswordError] = useState(null);
  const [formError, setFormError] = useState(null);
  const [isSigningIn, setIsSigningIn] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  
  // Get the authentication methods
  const { login, isAuthenticated, user, isLoading, getSavedCredentials } = useAuth();
  const deviceInfo = getDeviceInfo();

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword)
  }

  const API = process.env.REACT_APP_API_URL;
  const LMS_API = process.env.REACT_APP_API_LMS;
  const API_TOKEN = process.env.REACT_APP_API_TOKEN;
  const OLD_LMS = process.env.REACT_APP_OLD_LMS;

  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const encryptToken = (token) => {
    try {
      const encrypted = CryptoJS.AES.encrypt(token, process.env.REACT_APP_ENCRYPTION_KEY).toString();
      return encodeURIComponent(encrypted);
    } catch (error) {
      console.error("Token encryption failed:", error);
      return null;
    }
  };

  // Auto-fill saved credentials
  useEffect(() => {
    const credentials = getSavedCredentials();
    if (credentials) {
      if (emailRef.current) emailRef.current.value = credentials.email;
      if (passwordRef.current) passwordRef.current.value = credentials.password;
      setRememberMe(true);
    }
  }, [getSavedCredentials]);

  // Redirect if already authenticated
  useEffect(() => {
    if (isLoading) return;
    
    if (isAuthenticated && user) {
      const userRoleLower = user.role?.toLowerCase();
      
      // Handle external redirects for other roles
      if (userRoleLower !== "student" && userRoleLower !== "institute") {
        const redirectUrl = getRedirectUrl(user.role, user.token);
        if (redirectUrl) {
          window.location.href = redirectUrl;
          return;
        }
      }
      
      // Navigate to dashboard for student/institute
      const dashboardPath = userRoleLower === "institute" 
        ? "//institute-dashboard" 
        : "/dashboard";
      
      console.log('Already authenticated, redirecting to:', dashboardPath);
      navigate(dashboardPath, { replace: true });
    }
  }, [isAuthenticated, isLoading, user, navigate]);

  // Function to generate device ID
  const generateDeviceId = () => {
    const timestamp = Date.now().toString();
    const random = Math.random().toString(36).substring(2, 15);
    return `dev_${timestamp}_${random}`;
  };
  
  const registerDevice = async (userId, fcmtoken) => {
    try {
      const deviceData = {
        user_id: parseInt(userId),
        registration_id: fcmtoken,
        device_id: generateDeviceId(),
        status: "active",
        device_model: deviceInfo || "Unknown OS Version",
        device_manufacturer: browserName || "Unknown Browser",
      };

      const response = await fetch(`${LMS_API}/api/add-device`, {
        method: "POST",
        headers: {
          Authorization: API_TOKEN,
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify(deviceData),
      });

      const result = await response.json();

      if (response.ok) {
        console.log("Device registered successfully");
      } else {
        console.error("Device registration failed:", result);
      }
    } catch (error) {
      console.error("Device registration error:", error);
    }
  };

  // Function to determine redirect URL based on user role
  const getRedirectUrl = (role, token) => {
    if (!role) {
      console.log("No role provided");
      return null;
    }
    const normalizedRole = role.toString().trim();

    const encryptedToken = encryptToken(token);
    if (!encryptedToken) {
      console.error("Failed to encrypt token");
      return null;
    }

    switch (normalizedRole) {
      case "Organisation":
        return `${OLD_LMS}organisation/dashboard?token=${token}&role=${normalizedRole}`;
      case "Institute faculty":
        return `${OLD_LMS}faculty/students?token=${token}&role=${normalizedRole}`;
      case "Teacher":
        return `${OLD_LMS}teacher/dashboard?token=${token}&role=${normalizedRole}`;
      case "Student":
      case "Institute":
      case "Teacher":
        return null;
      default:
        console.log("Unknown role:", normalizedRole);
        return null;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const email = emailRef.current.value;
    const password = passwordRef.current.value;

    setEmailError(null);
    setPasswordError(null);
    setFormError(null);

    if (!validateEmail(email)) {
      setEmailError("Please enter a valid email address");
      return;
    }

    if (password.length < 8) {
      setPasswordError("Password must be at least 8 characters long");
      return;
    }

    setIsSigningIn(true);

    try {
      const response = await fetch(`${API}/api/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: email?.trim(),
          password: password?.trim()
        })
        // body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

      if (!response.ok) {
        switch (data.error_code) {
          case "EMAIL_NOT_REGISTERED":
            setEmailError("This email is not registered. Please sign up first.");
            break;
          case "INVALID_EMAIL":
            setEmailError("Invalid email format");
            break;
          case "INVALID_PASSWORD":
            setPasswordError("Incorrect password");
            break;
          case "ACCOUNT_DISABLED":
            setFormError("Your account has been disabled. Please contact support.");
            break;
          case "EMAIL_NOT_VERIFIED":
            setEmailError("Please verify your email before verification");
            break;
          default:
            if (data.message?.toLowerCase().includes("email")) {
              setEmailError(data.message);
            } else if (data.message?.toLowerCase().includes("password")) {
              setPasswordError(data.message);
            } else {
              setFormError(data.message || "An unexpected error occurred");
            }
        }
        return;
      }

      // Prepare session data
      const sessionPayload = {
        token: data.result.token,
        id: data.result.id,
        role: data.result.role,
        instituteType: data.result.instituteType || "",
        program_category_id: data.result.program_category_id || "",
        first_name: data.result.first_name || "",
        last_name: data.result.last_name || "",
        email: data.result.email || "",
        program_category: data.result.program_category || "",
        profile_image: data.result.profile_image || "",
        studentInstituteId: data.result.studentInstituteId || "",
        tour: data.result.tour,
        batchId: data.result.batchId || "",
        ...(data.result.role === "Institute" && data.result.instituteId && {
          instituteId: data.result.instituteId,
          instituteLogo: data.result.instituteLogo || "",
        }),
        degree: data.result.degree || "",
        location :data.result.location || "",
        instituteLogo: data.result.instituteLogo || "",
        hasbatch: data.result.hasbatch,
        batch_expiry_date: data.result.batch_expiry_date || ""
      };

      // Store session data
      sessionStorage.setItem("token", data.result.token);
      sessionStorage.setItem("id", data.result.id);
      sessionStorage.setItem("role", data.result.role);
      sessionStorage.setItem("instituteType", data.result.instituteType || "");
      sessionStorage.setItem("program_category_id", data.result.program_category_id || "");
      sessionStorage.setItem("first_name", data.result.first_name || "");
      sessionStorage.setItem("last_name", data.result.last_name || "");
      sessionStorage.setItem("email", data.result.email || "");
      sessionStorage.setItem("program_category", data.result.program_category || "");
      sessionStorage.setItem("profile_image", data.result.profile_image || "");
      sessionStorage.setItem("studentInstituteId", data.result.studentInstituteId || "");
      sessionStorage.setItem("tour", data.result.tour);
      sessionStorage.setItem("batchId", data.result.batchId || "");
      sessionStorage.setItem("degree", data.result.degree || "");
      sessionStorage.setItem("location", data.result.location || "");
      sessionStorage.setItem("instituteLogo", data.result.instituteLogo || "");
      sessionStorage.setItem("hasbatch", data.result.hasbatch || false);
      sessionStorage.setItem("batch_expiry_date", data.result.batch_expiry_date || "");

      if (data.result.role === "Institute" && data.result.instituteId) {
        sessionStorage.setItem("instituteId", data.result.instituteId);
        sessionStorage.setItem("instituteLogo", data.result.instituteLogo || "");
      }

      // Firebase messaging
      const messaging = await messagingPromise;

      if (messaging) {
        try {
          const firebaseToken = await getFirebaseToken(messaging, {
            vapidKey: process.env.REACT_APP_VAPID_KEY,
          });

          if (firebaseToken) {
            await registerDevice(data?.result.id, firebaseToken);

            onMessage(messaging, (payload) => {
              console.log("Message received in foreground:", payload);
            });
          } else {
            console.warn("FCM: Token generation failed.");
          }
        } catch (fcmError) {
          console.error("FCM setup error:", fcmError);
        }
      } else {
        console.warn("FCM is not supported on this device.");
      }

      // Broadcast to other tabs
      if ("BroadcastChannel" in window) {
        const bc = new BroadcastChannel("auth_channel");
        bc.postMessage({
          type: "AUTH_SYNC",
          data: sessionPayload,
        });
        bc.close();
      }

      const userRole = data.result.role?.toString().trim();
      const userRoleLower = userRole?.toLowerCase();

      // Use the auth context login method with remember me
      login(data.result.token, {
        id: data.result.id,
        role: data.result.role,
        program_category_id: data.result.program_category_id,
        email: email,
        instituteType: data.result.instituteType || "",
        first_name: data.result.first_name || "",
        last_name: data.result.last_name || "",
        program_category: data.result.program_category || "",
        profile_image: data.result.profile_image || "",
        studentInstituteId: data.result.studentInstituteId || "",
        tour: data.result.tour,
        batchId: data.result.batchId || "",
        instituteLogo: data.result.instituteLogo || "",
        ...(userRoleLower === "institute" && data.result.instituteId && { 
          instituteId: data.result.instituteId,
          instituteLogo: data.result.instituteLogo || "",
        }),
        hasbatch: data.result.hasbatch,
        batch_expiry_date: data.result.batch_expiry_date || ""
      }, rememberMe, password); // Pass rememberMe and password to useAuth

      // Redirect externally only for roles other than student/institute
      if (userRoleLower !== "student" && userRoleLower !== "institute" && userRoleLower !== "teacher") {
        const redirectUrl = getRedirectUrl(userRole, data.result.token);
        if (redirectUrl) {
          window.location.href = redirectUrl;
          return;
        }
      }

      // Navigate to dashboard
      setTimeout(() => {
        // const dashboardPath = userRoleLower === "institute" ? "/institute-dashboard" : "/dashboard";
        // console.log('Navigating to:', dashboardPath, 'for role:', userRole);
        const dashboardPath =
          userRoleLower === "institute"
            ? "/institute-dashboard"
            : userRoleLower === "teacher"
            ? "/teacher-dashboard"
            : "/dashboard";
        navigate(dashboardPath, { replace: true });
      }, 50);

    } catch (networkError) {
      console.error("Login error:", networkError);
      setFormError("Login failed. Please check your email and password is correct.");
    } finally {
      setIsSigningIn(false);
    }
  };

  // Show loading while checking auth
  if (isLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '100vh' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="min-vh-100 overflow-hidden">
        <div className="container-fluid h-100 p-0">
          <div className="row g-0 h-100 flex-nowrap">
            <AuthSlider/>
            <div className="col-lg-6 offset-lg-6 col-12 d-flex align-items-center justify-content-center bg-white" style={{ minHeight: isMobile ? "80vh":"100vh", overflowY: "auto" }}>
              <div className="w-100 p-4 p-md-0" style={{ maxWidth: "450px" }}>
                <div className="d-flex justify-content-between align-items-center mb-4 mb-lg-5">
                  <div className="d-flex align-items-center">
                    <div className="d-flex align-items-center justify-content-center me-3">
                      <img src="/assets/images/certificate/certificate_logo.png" width={180} height={40}/>
                    </div>
                  </div>
                </div>

                <div className="login-form">
                  <h3 className="mb-4 fw-bold text-balance" style={{ color: "#2D3748", fontSize: "2rem", lineHeight: "1.3" }}>
                    Sign into Your Account
                  </h3>
                  <form onSubmit={handleSubmit}>
                    <div className="mb-3">
                      <label
                        htmlFor="email"
                        className="form-label fw-semibold mb-2"
                        style={{ color: "#4A5568", fontSize: "0.95rem" }}
                      >
                        Email <span style={{ color: "#260efcff" }}>*</span>
                      </label>
                      <div className="position-relative">
                        <input
                          type="email"
                          ref={emailRef}
                          className={`form-control form-control-lg pe-5 ${emailError ? "is-invalid" : ""}`}
                          id="email"
                          placeholder=""
                          style={{
                            border: "2px solid #E2E8F0",
                            borderRadius: "12px",
                            padding: "14px 50px 14px 16px",
                            fontSize: "1rem",
                            transition: "border-color 0.2s ease",
                          }}
                          onFocus={(e) => (e.target.style.borderColor = "#260efcff")}
                          onBlur={(e) => (e.target.style.borderColor = "#E2E8F0")}
                        />
                        {!emailError &&
                        <Mail
                          size={20}
                          className="position-absolute top-50 end-0 translate-middle-y me-3"
                          style={{ color: "#A0AEC0" }}
                        />}
                      </div>
                      {emailError && <div className="invalid-feedback">{emailError}</div>}
                    </div>

                    <div className="mb-3">
                      <label
                        htmlFor="password"
                        className="form-label fw-semibold mb-2"
                        style={{ color: "#4A5568", fontSize: "0.95rem" }}
                      >
                        Password <span style={{ color: "#260efcff" }}>*</span>
                      </label>
                      <div className="position-relative">
                        <input
                          type={showPassword ? "text" : "password"}
                          className={`form-control form-control-lg pe-5 ${passwordError ? "is-invalid" : ""}`}
                          id="password"
                          ref={passwordRef}
                          placeholder=""
                          style={{
                            border: "2px solid #E2E8F0",
                            borderRadius: "12px",
                            padding: "14px 50px 14px 16px",
                            fontSize: "1rem",
                            transition: "border-color 0.2s ease",
                          }}
                          onFocus={(e) => (e.target.style.borderColor = "#260efcff")}
                          onBlur={(e) => (e.target.style.borderColor = "#E2E8F0")}
                        />
                        {!passwordError &&
                        <button
                          type="button"
                          className="btn border-0 bg-transparent position-absolute top-50 end-0 translate-middle-y me-2"
                          onClick={togglePasswordVisibility}
                          style={{ zIndex: 10 }}
                        >
                          {showPassword ? (
                            <EyeOff size={20} style={{ color: "#A0AEC0" }} />
                          ) : (
                            <Eye size={20} style={{ color: "#A0AEC0" }} />
                          )}
                        </button>}
                      </div>
                      {passwordError && (
                        <div className="text-danger mt-2">{passwordError}</div>
                      )}
                    </div>

                    <div className="d-flex justify-content-between align-items-center mb-4">
                      <div className="form-check">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          id="rememberMe"
                          checked={rememberMe}
                          onChange={(e) => setRememberMe(e.target.checked)}
                          style={{
                            width: "18px",
                            height: "18px",
                            cursor: "pointer",
                            borderColor: "#260efcff"
                          }}
                        />
                        <label
                          className="form-check-label ms-2"
                          htmlFor="rememberMe"
                          style={{ color: "#4A5568", fontSize: "0.9rem", cursor: "pointer" }}
                        >
                          Remember me
                        </label>
                      </div>
                      <Link to="/forgot-password" className="text-decoration-none fw-medium" style={{ color: "#260efcff", fontSize: "0.9rem" }}>
                        Forgot password?
                      </Link>
                    </div>

                    {formError && (
                      <div className="text-danger mt-3 mb-3 text-center">{formError}</div>
                    )}

                    <button
                      type="submit"
                      className="btn btn-lg w-100 mb-4 text-white d-flex align-items-center justify-content-center fw-semibold"
                      style={{
                        background: "linear-gradient(135deg, #260efcff 0%, #2b05ffff 80%)",
                        backgroundColor: "#260efcff !important",
                        border: "none",
                        borderRadius: "12px",
                        padding: "14px",
                        fontSize: "1.1rem",
                        transition: "transform 0.2s ease, box-shadow 0.2s ease",
                      }}
                      disabled={isSigningIn}
                    >
                      {isSigningIn ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2"></span>
                          Signing in...
                        </>
                      ) : (<>Sign In <ArrowRight size={20} className="ms-2" /></>)}
                    </button>

                    <div className="text-center">
                      <span className="fw-medium" style={{ color: "#0d0d0eff", fontSize: "0.95rem" }}>
                        Don't you have an account?{" "}
                      </span>
                      <Link
                        to="/register"
                        className="text-decoration-none fw-semibold"
                        style={{ color: "#260efcff", fontSize: "0.95rem" }}
                      >
                        Signup
                      </Link>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default NewLogin